/**
 * @file useScanFeedbackSetting.ts
 * @description Настройка «звук и вибрация при скане» как реактивное значение
 * (phase19.24 этап 1, шаг 1).
 *
 * Настройка одна на устройство и живёт в `localStorage`
 * (`lib/scan-feedback.ts`), но переключателей у неё теперь два: «Настройки
 * склада → Рабочее место» и пункт «Звук скана» в «⋯» складского документа.
 * Хук подписывается на общую шину, поэтому оба переключателя и индикатор в
 * поле скана всегда показывают одно и то же — без перезагрузки страницы.
 *
 * `useSyncExternalStore` вместо `useState`: значение живёт вне React, и
 * снимок должен читаться в момент рендера, иначе первый кадр после переключения
 * показывает старое состояние.
 */

import { useCallback, useSyncExternalStore } from "react";

import {
  isScanFeedbackEnabled,
  setScanFeedbackEnabled,
  subscribeScanFeedback,
} from "@/lib/scan-feedback";

interface ScanFeedbackSetting {
  /** true — на скан отвечаем бипом и вибро. По умолчанию включено. */
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  toggle: () => void;
}

/** На сервере (тесты без jsdom) звук считаем включённым — как в `lib`. */
function serverSnapshot(): boolean {
  return true;
}

export function useScanFeedbackSetting(): ScanFeedbackSetting {
  const enabled = useSyncExternalStore(
    subscribeScanFeedback,
    isScanFeedbackEnabled,
    serverSnapshot,
  );
  const setEnabled = useCallback((next: boolean) => {
    setScanFeedbackEnabled(next);
  }, []);
  const toggle = useCallback(() => {
    setScanFeedbackEnabled(!isScanFeedbackEnabled());
  }, []);
  return { enabled, setEnabled, toggle };
}
